import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { View } from '../types';
import { addPeriod, periodLabel, toDate } from '../utils';
import { RevenueHistoryData } from '../types';
import VatToggle from './VatToggle';
import DateRangeSelector from '../../shared/DateRangeSelector';
import { useTimeIntervalStore } from '../../../stores/appTimeIntervalStore';

interface HeaderControlsProps {
  view: View;
  setView: (v: View) => void;
  cursor: Date;
  setCursor: (d: Date) => void;
  revenueHistory: RevenueHistoryData[];
  hideVatToggle?: boolean;
}

export const HeaderControls: React.FC<HeaderControlsProps> = ({
  view,
  setView,
  cursor,
  setCursor,
  revenueHistory,
  hideVatToggle = false
}) => {
  const { t, i18n } = useTranslation();
  const label = periodLabel(cursor, view, i18n.language);

  const customStartDate = useTimeIntervalStore(s => s.customStartDate);
  const customEndDate = useTimeIntervalStore(s => s.customEndDate);
  const setCustomStartDate = useTimeIntervalStore(s => s.setCustomStartDate);
  const setCustomEndDate = useTimeIntervalStore(s => s.setCustomEndDate);

  // Calculate min and max dates from revenue history
  const { minDate, maxDate } = useMemo(() => {
    if (revenueHistory.length === 0) {
      return { minDate: new Date(), maxDate: new Date() };
    }

    const dates = revenueHistory.map(r => toDate(r.date));
    const min = new Date(Math.min(...dates.map(d => d.getTime())));
    const max = new Date(Math.max(...dates.map(d => d.getTime())));
    min.setHours(0, 0, 0, 0);
    max.setHours(0, 0, 0, 0);

    return { minDate: min, maxDate: max };
  }, [revenueHistory]);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        {view !== "all" && (
          <>
            <button
              className="p-2 rounded-md bg-card dark:bg-card-dark hover:bg-muted/10 dark:hover:bg-muted-dark/10 text-zinc-900 dark:text-zinc-50"
              onClick={() => setCursor(addPeriod(cursor, view, -1))}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="px-3 py-1.5 rounded-lg bg-white dark:bg-card-dark shadow text-sm font-medium text-zinc-900 dark:text-zinc-50">
              {label}
            </div>
            <button
              className="p-2 rounded-md bg-card dark:bg-card-dark hover:bg-muted/10 dark:hover:bg-muted-dark/10 text-zinc-900 dark:text-zinc-50"
              onClick={() => setCursor(addPeriod(cursor, view, 1))}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </>
        )}
        {view === "all" && (
          <DateRangeSelector
            startDate={customStartDate}
            endDate={customEndDate}
            onStartDateChange={setCustomStartDate}
            onEndDateChange={setCustomEndDate}
            minDate={minDate}
            maxDate={maxDate}
          />
        )}
      </div>

      <div className="flex items-center gap-3">
        {!hideVatToggle && <VatToggle />}
        <div className="flex gap-1 bg-muted/10 dark:bg-muted-dark/10 rounded-lg p-1">
          {(['day', 'week', 'month', 'quarter', 'all'] as View[]).map(v => (
            <button
              key={v}
              className={`px-3 py-1 rounded-md text-sm font-medium transition text-zinc-900 dark:text-zinc-50 ${
                view === v
                  ? 'bg-white dark:bg-card-dark shadow'
                  : 'hover:bg-white/50 dark:hover:bg-card-dark/50'
              }`}
              onClick={() => setView(v)}
            >
              {t(`revenueHistory.${v}`)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeaderControls;